import React, { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  Text,
  useColorModeValue,
  VStack,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  SimpleGrid,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { useCustomToast } from '../utils/customToast';
import {
  FaCog,
  FaCreditCard,
  FaLightbulb,
  FaRegClock,
  FaDesktop,
  FaInfoCircle,
  FaSignOutAlt,
  FaUser,
  FaBuilding,
  FaStar,
  FaChartBar,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { useUser } from '../contexts/UserContext';
import { updateUserProfile } from '../services/userService';
import { createCheckoutSession, createPortalSession, getSubscriptionStatus } from '../services/billingService';

const AGENCY_BASE_PRICE = 497;
const AGENCY_PER_UNIT_PRICE = 90;
const REGULAR_PRICE = 175;
const REGULAR_INTRO_PRICE = 17.5;

const statusColorScheme = (status) => {
  if (status === 'active') return 'green';
  if (status === 'trialing') return 'teal';
  if (status === 'past_due') return 'orange';
  return 'red';
};

const statusLabel = (status) => {
  if (status === 'trialing') return 'Free Trial';
  return status;
};

/* ── Agency Owner Billing Section ── */
const AgencyBillingSection = ({
  subscriptionStatus,
  borderColor,
  isBillingActionLoading,
  onSubscribe,
  onManageBilling,
}) => {
  const clientCount = subscriptionStatus?.agency_client_count || 0;
  const userCount = subscriptionStatus?.agency_user_count || 0;
  const isTrialing = subscriptionStatus?.status === 'trialing';
  const baseCost = isTrialing ? 0 : AGENCY_BASE_PRICE;
  const clientCost = clientCount * AGENCY_PER_UNIT_PRICE;
  const userCost = userCount * AGENCY_PER_UNIT_PRICE;
  const estimatedTotal = baseCost + clientCost + userCost;

  const LineItem = ({ label, amount, note }) => (
    <Flex justify="space-between" align="center" py={2}>
      <VStack align="flex-start" spacing={0}>
        <Text fontSize="sm">{label}</Text>
        {note && <Text fontSize="xs" color="gray.500">{note}</Text>}
      </VStack>
      <Text fontWeight="semibold" fontSize="sm">
        {isTrialing && label === 'Base Plan' ? 'Free Trial' : `$${amount.toLocaleString()}/mo`}
      </Text>
    </Flex>
  );

  if (subscriptionStatus?.has_subscription) {
    return (
      <VStack spacing={4} align="stretch">
        <SimpleGrid columns={2} spacing={4}>
          <Stat p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <StatLabel>Status</StatLabel>
            <StatNumber>
              <Badge
                colorScheme={statusColorScheme(subscriptionStatus.status)}
                fontSize="md"
                px={2}
                py={1}
                textTransform="capitalize"
              >
                {statusLabel(subscriptionStatus.status)}
              </Badge>
            </StatNumber>
          </Stat>
          <Stat p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <StatLabel>Plan</StatLabel>
            <StatNumber fontSize="lg">
              {subscriptionStatus.plan || 'Agency Plan'}
            </StatNumber>
          </Stat>
        </SimpleGrid>

        {/* Agency line-item breakdown */}
        <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
          <Text fontWeight="semibold" fontSize="sm" mb={3} color="gray.700">
            Billing Breakdown
          </Text>
          <Divider mb={2} />
          <LineItem
            label="Base Plan"
            amount={AGENCY_BASE_PRICE}
          />
          <LineItem
            label={`Clients (${clientCount})`}
            amount={clientCost}
            note={`${clientCount} × $${AGENCY_PER_UNIT_PRICE}/mo`}
          />
          <LineItem
            label={`Users (${userCount})`}
            amount={userCost}
            note={`${userCount} × $${AGENCY_PER_UNIT_PRICE}/mo`}
          />
          <Divider mt={2} mb={2} />
          <Flex justify="space-between" align="center">
            <Text fontWeight="bold" fontSize="sm">
              {isTrialing ? 'Trial Total' : 'Monthly Total'}
            </Text>
            <Text fontWeight="bold" fontSize="md" color="blue.600">
              {isTrialing ? `$${(clientCost + userCost).toLocaleString()}/mo after trial` : `$${estimatedTotal.toLocaleString()}/mo`}
            </Text>
          </Flex>
        </Box>

        {subscriptionStatus.current_period_end && (
          <Flex
            justify="space-between"
            align="center"
            p={3}
            borderWidth="1px"
            borderRadius="md"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color="gray.600">
              {subscriptionStatus.cancel_at_period_end
                ? 'Access until'
                : isTrialing
                ? 'Trial ends'
                : 'Next billing date'}
            </Text>
            <Badge colorScheme="blue">
              {new Date(subscriptionStatus.current_period_end).toLocaleDateString()}
            </Badge>
          </Flex>
        )}

        {subscriptionStatus.cancel_at_period_end && (
          <Text fontSize="sm" color="orange.500">
            Your subscription will cancel at the end of the current period.
          </Text>
        )}

        <Button
          colorScheme="blue"
          variant="outline"
          leftIcon={<Icon as={FaExternalLinkAlt} />}
          onClick={onManageBilling}
          isLoading={isBillingActionLoading}
          loadingText="Opening..."
        >
          Manage Billing
        </Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box p={5} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
        <Text fontWeight="semibold" mb={1}>Agency Plan</Text>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Full white-label analytics platform for your clients.
        </Text>

        <Box borderWidth="1px" borderRadius="md" borderColor={borderColor} p={4} mb={4}>
          <Text fontWeight="semibold" fontSize="sm" mb={3} color="gray.700">
            Pricing Breakdown
          </Text>
          <Divider mb={2} />
          <Flex justify="space-between" py={2}>
            <Text fontSize="sm">Base Plan</Text>
            <Text fontWeight="semibold" fontSize="sm">${AGENCY_BASE_PRICE}/mo</Text>
          </Flex>
          <Flex justify="space-between" py={2}>
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="sm">Clients ({clientCount})</Text>
              <Text fontSize="xs" color="gray.500">{clientCount} × ${AGENCY_PER_UNIT_PRICE}/mo</Text>
            </VStack>
            <Text fontWeight="semibold" fontSize="sm">${clientCost}/mo</Text>
          </Flex>
          <Flex justify="space-between" py={2}>
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="sm">Users ({userCount})</Text>
              <Text fontSize="xs" color="gray.500">{userCount} × ${AGENCY_PER_UNIT_PRICE}/mo</Text>
            </VStack>
            <Text fontWeight="semibold" fontSize="sm">${userCost}/mo</Text>
          </Flex>
          <Divider mt={2} mb={2} />
          <Flex justify="space-between">
            <Text fontWeight="bold" fontSize="sm">Estimated Total</Text>
            <Text fontWeight="bold" fontSize="md" color="blue.600">
              ${estimatedTotal.toLocaleString()}/mo
            </Text>
          </Flex>
        </Box>

        <Button
          colorScheme="blue"
          onClick={onSubscribe}
          isLoading={isBillingActionLoading}
          loadingText="Starting checkout..."
          size="lg"
          w="full"
        >
          Subscribe to Agency Plan
        </Button>
        <Text fontSize="xs" color="gray.400" mt={2} textAlign="center">
          Free trial available — contact us to learn more
        </Text>
      </Box>
    </VStack>
  );
};

/* ── Regular User Billing Section ── */
const RegularBillingSection = ({
  subscriptionStatus,
  borderColor,
  isBillingActionLoading,
  onSubscribe,
  onManageBilling,
}) => {
  if (subscriptionStatus?.has_subscription) {
    return (
      <VStack spacing={4} align="stretch">
        <SimpleGrid columns={2} spacing={4}>
          <Stat p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <StatLabel>Status</StatLabel>
            <StatNumber>
              <Badge
                colorScheme={statusColorScheme(subscriptionStatus.status)}
                fontSize="md"
                px={2}
                py={1}
                textTransform="capitalize"
              >
                {statusLabel(subscriptionStatus.status)}
              </Badge>
            </StatNumber>
          </Stat>
          <Stat p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <StatLabel>Plan</StatLabel>
            <StatNumber fontSize="lg">
              {subscriptionStatus.plan || 'Full Access'}
            </StatNumber>
            <StatHelpText>${REGULAR_PRICE}/mo</StatHelpText>
          </Stat>
        </SimpleGrid>

        {subscriptionStatus.current_period_end && (
          <Flex
            justify="space-between"
            align="center"
            p={3}
            borderWidth="1px"
            borderRadius="md"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color="gray.600">
              {subscriptionStatus.cancel_at_period_end ? 'Access until' : 'Next billing date'}
            </Text>
            <Badge colorScheme="blue">
              {new Date(subscriptionStatus.current_period_end).toLocaleDateString()}
            </Badge>
          </Flex>
        )}

        {subscriptionStatus.cancel_at_period_end && (
          <Text fontSize="sm" color="orange.500">
            Your subscription will cancel at the end of the current period.
          </Text>
        )}

        <Button
          colorScheme="blue"
          variant="outline"
          leftIcon={<Icon as={FaExternalLinkAlt} />}
          onClick={onManageBilling}
          isLoading={isBillingActionLoading}
          loadingText="Opening..."
        >
          Manage Billing
        </Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box
        p={5}
        borderWidth="1px"
        borderRadius="lg"
        borderColor={borderColor}
        textAlign="center"
      >
        <Text color="gray.600" mb={2}>
          No active subscription
        </Text>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Subscribe to get full access to all Localytics features.
        </Text>
        <Button
          colorScheme="blue"
          onClick={onSubscribe}
          isLoading={isBillingActionLoading}
          loadingText="Starting checkout..."
          size="lg"
          w="full"
        >
          Subscribe — ${REGULAR_PRICE}/mo
        </Button>
        <Text fontSize="xs" color="gray.400" mt={2}>
          Intro offer: ${REGULAR_INTRO_PRICE} for the first month
        </Text>
      </Box>
    </VStack>
  );
};

const UserSettingsModal = ({ isOpen, onClose }) => {
  const {
    userProfile,
    displayName,
    firstName,
    lastName,
    email,
    vertical,
    stats,
    activity,
    assignedBusinesses,
    favoriteBusinesses,
    toggleFavorite,
    isFavorited,
    refreshUserProfile,
    isAgencyOwner,
  } = useUser();

  // Profile form state
  const [editedDisplayName, setEditedDisplayName] = useState('');
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Billing state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [isBillingActionLoading, setIsBillingActionLoading] = useState(false);

  // Preferences state
  const [tipsEnabled, setTipsEnabled] = useState(true);
  const [tipFrequency, setTipFrequency] = useState('medium');
  const [welcomeMessageReset, setWelcomeMessageReset] = useState(false);

  const toast = useCustomToast();
  const navigate = useNavigate();

  // Theme colors
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const tabBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardHoverBg = useColorModeValue('gray.50', 'gray.700');
  
  // Load settings from localStorage and user profile on component mount
  useEffect(() => {
    if (isOpen) {
      console.log('UserSettingsModal opened', { userProfile, displayName, firstName, lastName, email, vertical });

      // Load profile data - use direct values from userProfile if context values are empty
      const _displayName = displayName || userProfile?.display_name || '';
      const _firstName = firstName || userProfile?.first_name || '';
      const _lastName = lastName || userProfile?.last_name || '';

      setEditedDisplayName(_displayName);
      setEditedFirstName(_firstName);
      setEditedLastName(_lastName);

      // Load preferences from localStorage
      const storedTipsEnabled = localStorage.getItem('tipsEnabled');
      if (storedTipsEnabled !== null) {
        setTipsEnabled(storedTipsEnabled === 'true');
      }

      const storedTipFrequency = localStorage.getItem('tipFrequency');
      if (storedTipFrequency) {
        setTipFrequency(storedTipFrequency);
      }

      // Fetch billing status
      setIsBillingLoading(true);
      getSubscriptionStatus()
        .then((status) => setSubscriptionStatus(status))
        .catch((err) => console.error('Failed to fetch subscription status:', err))
        .finally(() => setIsBillingLoading(false));
    }
  }, [isOpen, userProfile, displayName, firstName, lastName, email, vertical]);

  // Handle profile update
  const handleSaveProfile = async () => {
    try {
      setIsSavingProfile(true);

      await updateUserProfile({
        display_name: editedDisplayName.trim(),
        first_name: editedFirstName.trim(),
        last_name: editedLastName.trim(),
      });

      await refreshUserProfile();

      toast({
        title: 'Profile updated',
        description: 'Your profile information has been saved.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update profile. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Handle preferences save
  const handleSavePreferences = () => {
    // Save settings to localStorage
    localStorage.setItem('tipsEnabled', tipsEnabled.toString());
    localStorage.setItem('tipFrequency', tipFrequency);

    // Handle welcome message reset if needed
    if (welcomeMessageReset) {
      localStorage.removeItem('hasSeenWelcome');
      setWelcomeMessageReset(false);
    }

    toast({
      title: 'Preferences saved',
      description: 'Your preferences have been updated.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Handle favorite toggle (users can only have one favorite)
  const handleToggleFavorite = async (mapsUri) => {
    try {
      const result = await toggleFavorite(mapsUri);
      if (result.isNowFavorited) {
        toast({
          title: 'Great Choice! 🎉',
          description: 'Your favorite will now be ⭐ starred on all dashboards',
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Favorite removed',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to update favorite status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // Handle subscribe - redirect to Stripe Checkout
  const handleSubscribe = async () => {
    try {
      setIsBillingActionLoading(true);
      const successUrl = `${window.location.origin}/dashboard?billing=success`;
      const cancelUrl = `${window.location.origin}/dashboard?billing=canceled`;
      const { checkout_url } = await createCheckoutSession(successUrl, cancelUrl);
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Checkout error',
        description: 'Failed to start checkout. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsBillingActionLoading(false);
    }
  };

  // Handle manage billing - redirect to Stripe Customer Portal
  const handleManageBilling = async () => {
    try {
      setIsBillingActionLoading(true);
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast({
        title: 'Billing error',
        description: 'Failed to open billing portal. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsBillingActionLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      
      // Show toast notification
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      
      onClose();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      
      toast({
        title: 'Logout failed',
        description: 'There was an error logging out. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg={headerBg} borderTopRadius="md">
          <HStack>
            <Icon as={FaCog} mr={2} />
            <Text>User Settings</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={0}>
          <Tabs colorScheme="blue" variant="enclosed">
            <TabList px={6} pt={4} bg={tabBg}>
              <Tab>
                <Icon as={FaUser} mr={2} />
                Profile
              </Tab>
              <Tab>
                <Icon as={FaBuilding} mr={2} />
                My Businesses
              </Tab>
              <Tab>
                <Icon as={FaLightbulb} mr={2} />
                Preferences
              </Tab>
              <Tab>
                <Icon as={FaCreditCard} mr={2} />
                Billing
              </Tab>
              <Tab>
                <Icon as={FaChartBar} mr={2} />
                Stats
              </Tab>
            </TabList>

            <TabPanels>
              {/* Profile Tab */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="sm" mb={4}>
                      Profile Information
                    </Heading>

                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>Email</FormLabel>
                        <Input value={email} isReadOnly bg={useColorModeValue('gray.50', 'gray.700')} />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Your email cannot be changed
                        </Text>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Vertical</FormLabel>
                        <Input value={vertical} isReadOnly bg={useColorModeValue('gray.50', 'gray.700')} />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Contact support to change your vertical
                        </Text>
                      </FormControl>

                      <Divider />

                      <FormControl>
                        <FormLabel>Display Name</FormLabel>
                        <Input
                          value={editedDisplayName}
                          onChange={(e) => setEditedDisplayName(e.target.value)}
                          placeholder="How you want to be addressed"
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          This name will appear throughout the app
                        </Text>
                      </FormControl>

                      <SimpleGrid columns={2} spacing={4}>
                        <FormControl>
                          <FormLabel>First Name</FormLabel>
                          <Input
                            value={editedFirstName}
                            onChange={(e) => setEditedFirstName(e.target.value)}
                            placeholder="First name"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Last Name</FormLabel>
                          <Input
                            value={editedLastName}
                            onChange={(e) => setEditedLastName(e.target.value)}
                            placeholder="Last name"
                          />
                        </FormControl>
                      </SimpleGrid>

                      <Button
                        colorScheme="blue"
                        onClick={handleSaveProfile}
                        isLoading={isSavingProfile}
                        loadingText="Saving..."
                      >
                        Save Profile
                      </Button>
                    </VStack>
                  </Box>

                  <Divider />

                  <Box>
                    <Heading size="sm" mb={4} display="flex" alignItems="center">
                      <Icon as={FaSignOutAlt} color="red.400" mr={2} />
                      Account
                    </Heading>

                    <Button
                      colorScheme="red"
                      variant="outline"
                      leftIcon={<Icon as={FaSignOutAlt} />}
                      size="md"
                      width="full"
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </Box>
                </VStack>
              </TabPanel>

              {/* My Businesses Tab */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  {/* Favorite Business (users can only have one) */}
                  <Box>
                    <Heading size="sm" mb={4} display="flex" alignItems="center">
                      <Icon as={FaStar} color="yellow.400" mr={2} />
                      Favorite Business
                    </Heading>

                    {favoriteBusinesses && favoriteBusinesses.length > 0 ? (
                      <VStack spacing={3} align="stretch">
                        {favoriteBusinesses.map((business) => (
                          <Box
                            key={business.maps_uri}
                            p={4}
                            borderWidth="1px"
                            borderRadius="md"
                            borderColor={borderColor}
                            _hover={{ bg: cardHoverBg }}
                          >
                            <Flex align="center">
                              <Box flex={1}>
                                <Text fontWeight="semibold">{business.name}</Text>
                                <Text fontSize="sm" color="gray.500">
                                  {business.rating_avg ? `⭐ ${business.rating_avg.toFixed(1)} (${business.total_reviews} reviews)` : 'No rating'}
                                </Text>
                              </Box>
                              <IconButton
                                icon={<Icon as={FaStar} />}
                                colorScheme="yellow"
                                variant="ghost"
                                onClick={() => handleToggleFavorite(business.maps_uri)}
                                aria-label="Remove favorite"
                              />
                            </Flex>
                          </Box>
                        ))}
                      </VStack>
                    ) : (
                      <Text color="gray.500" fontSize="sm">
                        You haven't selected a favorite business yet. Click the star icon on any business card to set it as your favorite.
                      </Text>
                    )}
                  </Box>

                  <Divider />

                  {/* Assigned Businesses */}
                  <Box>
                    <Heading size="sm" mb={4} display="flex" alignItems="center">
                      <Icon as={FaBuilding} color="blue.400" mr={2} />
                      Assigned Businesses ({assignedBusinesses?.length || 0})
                    </Heading>

                    {assignedBusinesses && assignedBusinesses.length > 0 ? (
                      <VStack spacing={3} align="stretch">
                        {assignedBusinesses.map((business) => (
                          <Box
                            key={business.maps_uri}
                            p={4}
                            borderWidth="1px"
                            borderRadius="md"
                            borderColor={borderColor}
                          >
                            <Flex align="center">
                              <Box flex={1}>
                                <HStack>
                                  <Text fontWeight="semibold">{business.name}</Text>
                                  <Badge colorScheme="blue" fontSize="xs">
                                    {business.role || 'Member'}
                                  </Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.500">
                                  Assigned on {new Date(business.assigned_at).toLocaleDateString()}
                                </Text>
                              </Box>
                            </Flex>
                          </Box>
                        ))}
                      </VStack>
                    ) : (
                      <Text color="gray.500" fontSize="sm">
                        No businesses have been assigned to you yet. Contact your administrator to get assigned.
                      </Text>
                    )}
                  </Box>
                </VStack>
              </TabPanel>

              {/* Preferences Tab */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  {/* Tip Settings Section */}
                  <Box>
                    <Heading size="sm" mb={4} display="flex" alignItems="center">
                      <Icon as={FaLightbulb} color="yellow.400" mr={2} />
                      Platform Tips
                    </Heading>

                    <VStack spacing={4} align="stretch">
                      <FormControl display="flex" alignItems="center" justifyContent="space-between">
                        <FormLabel htmlFor="tips-toggle" mb={0} cursor="pointer">
                          <HStack>
                            <Text>Show helpful tips</Text>
                            <Icon as={FaInfoCircle} color="gray.400" />
                          </HStack>
                        </FormLabel>
                        <Switch
                          id="tips-toggle"
                          isChecked={tipsEnabled}
                          onChange={() => setTipsEnabled(!tipsEnabled)}
                          colorScheme="blue"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel htmlFor="tip-frequency" display="flex" alignItems="center">
                          <Icon as={FaRegClock} mr={2} color="gray.500" />
                          <Text>Tip frequency</Text>
                        </FormLabel>
                        <Select
                          id="tip-frequency"
                          value={tipFrequency}
                          onChange={(e) => setTipFrequency(e.target.value)}
                          isDisabled={!tipsEnabled}
                        >
                          <option value="low">Low (15 minutes)</option>
                          <option value="medium">Medium (5 minutes)</option>
                          <option value="high">High (2 minutes)</option>
                        </Select>
                      </FormControl>

                      <FormControl display="flex" alignItems="center" justifyContent="space-between">
                        <FormLabel htmlFor="welcome-reset" mb={0} cursor="pointer">
                          <Text>Reset welcome guide</Text>
                        </FormLabel>
                        <Checkbox
                          id="welcome-reset"
                          isChecked={welcomeMessageReset}
                          onChange={() => setWelcomeMessageReset(!welcomeMessageReset)}
                          colorScheme="blue"
                        />
                      </FormControl>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Display Settings Section */}
                  <Box>
                    <Heading size="sm" mb={4} display="flex" alignItems="center">
                      <Icon as={FaDesktop} color="blue.400" mr={2} />
                      Display Settings
                    </Heading>

                    <Text fontSize="sm" color="gray.500">
                      Display settings will be available in future updates.
                    </Text>
                  </Box>

                  <Button colorScheme="blue" onClick={handleSavePreferences}>
                    Save Preferences
                  </Button>
                </VStack>
              </TabPanel>

              {/* Billing Tab */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="sm" mb={4} display="flex" alignItems="center">
                      <Icon as={FaCreditCard} color="blue.400" mr={2} />
                      Subscription
                    </Heading>

                    {isBillingLoading ? (
                      <Text color="gray.500" fontSize="sm">Loading billing info...</Text>
                    ) : isAgencyOwner ? (
                      /* ── Agency Owner Billing ── */
                      <AgencyBillingSection
                        subscriptionStatus={subscriptionStatus}
                        borderColor={borderColor}
                        isBillingActionLoading={isBillingActionLoading}
                        onSubscribe={handleSubscribe}
                        onManageBilling={handleManageBilling}
                      />
                    ) : (
                      /* ── Regular User Billing ── */
                      <RegularBillingSection
                        subscriptionStatus={subscriptionStatus}
                        borderColor={borderColor}
                        isBillingActionLoading={isBillingActionLoading}
                        onSubscribe={handleSubscribe}
                        onManageBilling={handleManageBilling}
                      />
                    )}
                  </Box>
                </VStack>
              </TabPanel>

              {/* Stats Tab */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="sm" mb={4}>
                      Account Statistics
                    </Heading>

                    <SimpleGrid columns={2} spacing={4}>
                      <Stat
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={borderColor}
                      >
                        <StatLabel>Dashboard Views</StatLabel>
                        <StatNumber>{stats?.dashboard_views || 0}</StatNumber>
                        <StatHelpText>Total views</StatHelpText>
                      </Stat>

                      <Stat
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={borderColor}
                      >
                        <StatLabel>Login Streak</StatLabel>
                        <HStack spacing={2}>
                          <StatNumber>{activity?.login_streak_days || 0}</StatNumber>
                          {activity?.login_streak_days >= 3 && (
                            <Text fontSize="2xl" role="img" aria-label="fire">🔥</Text>
                          )}
                        </HStack>
                        <StatHelpText>
                          {activity?.login_streak_days > 0
                            ? `Best: ${activity?.longest_streak_days || 0} days`
                            : 'Start your streak today!'}
                        </StatHelpText>
                      </Stat>

                      <Stat
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={borderColor}
                      >
                        <StatLabel>Tasks Completed</StatLabel>
                        <StatNumber>{stats?.total_tasks_completed || 0}</StatNumber>
                        <StatHelpText>All time</StatHelpText>
                      </Stat>

                      <Stat
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={borderColor}
                      >
                        <StatLabel>Reports Generated</StatLabel>
                        <StatNumber>{stats?.reports_generated || 0}</StatNumber>
                        <StatHelpText>Total reports</StatHelpText>
                      </Stat>
                    </SimpleGrid>
                  </Box>

                  <Divider />

                  <Box>
                    <Heading size="sm" mb={4}>
                      Activity Summary
                    </Heading>

                    <VStack spacing={3} align="stretch">
                      <Flex justify="space-between" align="center">
                        <Text>Last Login</Text>
                        <Badge colorScheme="blue">
                          {activity?.last_login
                            ? new Date(activity.last_login).toLocaleDateString()
                            : 'Never'}
                        </Badge>
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text>Account Created</Text>
                        <Badge colorScheme="green">
                          {activity?.account_created
                            ? new Date(activity.account_created).toLocaleDateString()
                            : 'Unknown'}
                        </Badge>
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text>Total Sessions</Text>
                        <Badge>{activity?.total_sessions || 0}</Badge>
                      </Flex>
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UserSettingsModal;